import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMetroData } from '../hooks/useMetroData';
import { useMetroStore } from '../stores/metroStore';
import { useFilteredMetros } from '../hooks/useFilteredMetros';
import { useUrlParamSeed } from '../lib/use-url-param-seed';
import { useIsMobile } from '../lib/use-is-mobile';
import { useFocusTrap } from '../lib/use-focus-trap';
import { LeafletMap } from '../components/map/LeafletMap';
import { MapLegend } from '../components/map/MapLegend';
import { MapSidebar } from '../components/map/MapSidebar';
import { MapMobileLayout } from '../components/map/MapMobileLayout';
import { MapInsets } from '../components/map/MapInsets';
import { MetroPopup } from '../components/map/MetroPopup';
import { LoadingState } from '../components/shared/LoadingState';
import { ErrorState } from '../components/shared/ErrorState';
// 2026-07-02 (Georgetown feedback): first-visit IntroModal removed from
// every page. Reviewers found the interstitial disruptive; the pages
// carry enough affordance on their own. The component + its support
// modules were deleted in the follow-up dead-code sweep.
import { SectionNav } from '../components/layout/SectionNav';

/**
 * Map Explorer page. Full-viewport map shell per `_archive/map.html` lines
 * 112-116: `position: relative; height: calc(100vh - 108px); overflow:
 * hidden` (the prototype's own 108px deduction = topbar 56 + section-nav 52
 * — historical reference only; see below for the React build's numbers).
 *
 * Layout architecture (MAP-fix-1, locked-scope decision Q1=A):
 *   ┌─ Topbar (sticky, 74 desktop / 68 mobile) ───────┐ z-[360]
 *   ├─ SectionNav (sticky, 60) ──────────────────────┤ z-[350]
 *   ├─ Map shell (calc(100vh - var(--offset-nav-stack))┤
 *   │   ├─ MapSidebar (absolute, top-4 left-4)        │ z-400
 *   │   ├─ MapZoom (absolute, top-4 right-4)          │ z-400 (inside Leaflet)
 *   │   ├─ MapLegend (absolute, bottom-4 left-[320px])│ z-400
 *   │   └─ LeafletMap (fills the shell)               │ z-0
 *   └────────────────────────────────────────────────┘
 *
 * The map shell height reads `var(--offset-nav-stack)` directly (the
 * DEC-075 single-owner CSS var, TD-08a/b — `calc(var(--h-topbar) + 60px)`,
 * responsive to the Topbar's real desktop/mobile height) instead of a
 * numeric JS constant baked into the inline style, so it can't silently
 * drift out of sync with the Topbar below 1001px (CR-028/029 follow-up,
 * 2026-08-18). At desktop widths this resolves to 134 = 74 (Topbar,
 * CR-028/029 CEW chrome parity) + SectionNav's `h-[60px]` (matching
 * `_archive/home.html` line 255's 60, not the prototype map page's 52).
 * The two prototypes disagree. Since SectionNav is a shared component
 * across pages, a single height is the right call. We keep 60 (homepage
 * value) and adjust the map shell deduction accordingly. Without this,
 * the map shell overflows and the body grows a scrollbar — broken per
 * MAP-fix-14.
 *
 * No PageHeader, no surrounding card, no Footer (the Footer is
 * inappropriate when the map fills the viewport — there's nowhere to
 * scroll to). Loading/error states render full-viewport so users see
 * them immediately on cold start.
 */
export default function MapExplorer() {
  const { metros, isLoading, error, retry } = useMetroData();
  const { selectedMetroId, selectMetro } = useMetroStore();
  const isPopupExpanded = useMetroStore((s) => s.isPopupExpanded);
  const setPopupExpanded = useMetroStore((s) => s.setPopupExpanded);
  const filtered = useFilteredMetros(metros);
  // Resolve the selected metro for the desktop expand-modal below.
  // Memoized so the modal wrapper doesn't churn on every render.
  const expandedMetro = useMemo(
    () => (selectedMetroId ? metros.find((m) => m.id === selectedMetroId) ?? null : null),
    [metros, selectedMetroId],
  );
  const closeExpandedModal = useCallback(() => {
    setPopupExpanded(false);
    selectMetro(null);
  }, [selectMetro, setPopupExpanded]);
  // The map legend is hidden while a metro popup is open (to avoid
  // overlapping the popup) and must reappear when none is. We track that
  // as its OWN state driven by Leaflet's popupopen/popupclose events —
  // NOT off `selectedMetroId` — so switching directly from one metro's
  // open popup to another's keeps the legend hidden throughout (the old
  // popup's `popupclose` must not be mistaken for "no popup open"). See
  // LeafletMap's PopupStateWatcher for the batching rationale.
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Escape key closes the expand modal. Bound at the page level
  // (not on the modal card) so it works even when focus is inside
  // the sidebar or map controls.
  useEffect(() => {
    if (!isPopupExpanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeExpandedModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPopupExpanded, closeExpandedModal]);

  // 2026-07-06 (code-review finding #1, LUIS-BATCH-110-116-REVIEW.md):
  // the desktop expand modal is `role="dialog" aria-modal="true"` but
  // previously had no focus trap, no initial focus, no focus
  // restoration, and no body-scroll lock -- keyboard/SR users could
  // Tab out from behind it into the map + sidebar. The MOBILE twin
  // (`MapMobileLayout`) already implements this correctly; mirror the
  // same ref + effect pattern here rather than hand-rolling a new one.
  const isModalOpen = isPopupExpanded && expandedMetro !== null;
  const modalCardRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Trap Tab/Shift+Tab inside the dialog while it's open.
  useFocusTrap(modalCardRef, isModalOpen);

  // Body-scroll lock + initial focus into the dialog + focus
  // restoration to the trigger on close. Mirrors MapMobileLayout's
  // two effects (body-scroll/Escape is handled separately above,
  // pre-existing) combined here since both key off the same
  // `isModalOpen` boolean.
  useEffect(() => {
    if (!isModalOpen) return undefined;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Defer to next frame so the card has mounted before .focus().
    const handle = window.requestAnimationFrame(() => {
      modalCardRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(handle);
      document.body.style.overflow = previousOverflow;
      const prev = previousFocusRef.current;
      if (prev && prev.isConnected && typeof prev.focus === 'function') {
        prev.focus();
      }
      previousFocusRef.current = null;
    };
  }, [isModalOpen]);

  const isMobile = useIsMobile();

  // Cross-tab nav glue: when the Map page is opened with a
  // `?metroId=X` query (from a RowPeekDrawer "Locate on map" link
  // or the MetroPopup "View detail page" link redirected via Map),
  // pre-select that metro so the sidebar + map zoom snap to it.
  //
  // Seeded ONCE after metros load via the shared useUrlParamSeed
  // hook (DEC-061). The previous implementation re-fired on any
  // location.search change, which would stomp user state if an
  // unrelated query param changed -- a latent footgun caught in
  // PR #62 review and fixed here.
  useUrlParamSeed('metroId', metros.length > 0, (metroIdParam) => {
    if (metroIdParam && metros.some((m) => m.id === metroIdParam)) {
      selectMetro(metroIdParam);
    }
  });

  // Mobile (< 600px) takes a totally different shell: the desktop
  // pattern of a 288px sidebar absolutely positioned over a full-
  // viewport map covers nearly the entire screen on a narrow phone
  // and makes the map unusable. On mobile we drop into
  // `MapMobileLayout` instead, which stacks filters → map (50vh) →
  // scroll hint → metro list down the page in normal document flow.
  //
  // We branch on `isMobile` here (not via CSS) because mounting two
  // `LeafletMap`s would double-initialize Leaflet, and `display:
  // none` breaks Leaflet's tile-size measurement on whichever map
  // is hidden. Single mount, single layout — flipped by the media-
  // query hook on viewport change.
  return (
    <>
      <SectionNav />
      {isMobile ? (
        <div role="region" aria-label="Map Explorer">
          {isLoading && (
            <div className="flex h-[60vh] items-center justify-center">
              <LoadingState label="Loading metros…" />
            </div>
          )}
          {error && (
            <div className="flex h-[60vh] items-center justify-center">
              <ErrorState title="Cannot load metros" message={error.message} onRetry={retry} />
            </div>
          )}
          {!isLoading && !error && (
            <MapMobileLayout
              metros={metros}
              selectedMetroId={selectedMetroId}
              onSelectMetro={selectMetro}
            />
          )}
        </div>
      ) : (
        /* `<div role="region">` not `<main>` — SharedLayout already wraps
           every page in a single `<main id="main">` (the skip-link target).
           Nesting `<main>` violates WCAG (one main per document) and was
           flagged by code-reviewer agent in MAP-fix-7 review. */
        <div
          role="region"
          aria-label="Map Explorer"
          className="relative overflow-hidden"
          style={{ height: 'calc(100vh - var(--offset-nav-stack))' }}
        >
          {isLoading && (
            <div className="flex h-full items-center justify-center">
              <LoadingState label="Loading metros…" />
            </div>
          )}
          {error && (
            <div className="flex h-full items-center justify-center">
              <ErrorState title="Cannot load metros" message={error.message} onRetry={retry} />
            </div>
          )}
          {!isLoading && !error && (
            <>
              <LeafletMap
                metros={filtered}
                selectedMetroId={selectedMetroId}
                onSelectMetro={selectMetro}
                onPopupOpenChange={setIsPopupOpen}
              />
              <MapSidebar
                metros={metros}
                selectedMetroId={selectedMetroId}
                onSelectMetro={selectMetro}
              />
              {/* 2026-07-02 (V2 feedback follow-up): the top-center
                  legend was overlapping the Leaflet MetroPopup because
                  the legend sits at z-[500] in the outer stacking
                  context while the popup lives inside the Leaflet
                  container's own context. Rather than fight z-index
                  across two stacking contexts we just hide the legend
                  while a popup (or the fullscreen expand modal) is open.
                  CR-19 (2026-07-08): the gate is driven by `isPopupOpen`
                  (a real popupopen/popupclose signal), not by
                  `selectedMetroId`, so it comes back the moment the popup
                  closes AND stays hidden when the user switches straight
                  from one metro's popup to another's. Preserves the
                  top-center placement Georgetown asked for. */}
              {!isPopupOpen && !isPopupExpanded && <MapLegend />}
              {/* 2026-07-03 (V2 outstanding): AK / HI insets. Two
                  small pinned map cards in the bottom-left corner
                  so the map reads as "all 50 states represented"
                  even though the 55-metro dataset currently covers
                  only the contiguous US. Insets are read-only and
                  don't respond to pan/zoom -- reference framing
                  only. */}
              <MapInsets metros={filtered} />
              {/* 2026-07-03 (V2 feedback follow-up): fullscreen
                  expand-modal. Rendered on desktop when the user
                  clicks the "expand" icon in the compact Leaflet
                  popup (see MetroBubble). Mirrors the mobile treatment
                  from MapMobileLayout so both surfaces share the
                  MetroPopup body -- surface='modal' tells the popup
                  to let its inner table use the full viewport height
                  instead of the 340px cap it uses inside the Leaflet
                  chrome. */}
              {isModalOpen && expandedMetro && (
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label={`${expandedMetro.name} details`}
                  className="fixed inset-0 z-[5000] flex items-center justify-center bg-navy/40 backdrop-blur-[2px] px-4 py-8 transition-opacity duration-200 motion-reduce:transition-none"
                >
                  <div
                    aria-hidden="true"
                    onClick={closeExpandedModal}
                    className="absolute inset-0 cursor-pointer"
                  />
                  <div
                    ref={modalCardRef}
                    tabIndex={-1}
                    className="relative w-full max-w-[900px] h-[calc(100dvh_-_var(--modal-inset))] overflow-hidden rounded-overlay bg-white shadow-modal outline-none transition-transform duration-200 motion-reduce:transition-none"
                  >
                    <MetroPopup
                      metro={expandedMetro}
                      onClose={closeExpandedModal}
                      surface="modal"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

    </>
  );
}
